<template>
  <div class="py-6">
    <form @submit.prevent ref="form" class="space-y-6">
      <div class="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
        <h2 class="text-lg font-semibold text-gray-800">Édition du membre</h2>

        <!-- Nom -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700">
            Nom complet
          </label>
          <input
            required
            type="text"
            v-model="teammate.name"
            class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 shadow-theme-xs"
            placeholder="Ex: Raclara Zambo"
          />
        </div>

        <!-- Email -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            required
            type="email"
            v-model="teammate.email"
            :disabled="!!teammate.id"
            :class="[
              'w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 shadow-theme-xs',
              teammate.id ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
            ]"
            placeholder="exemple@email.com"
          />
          <p v-if="teammate.id" class="mt-1 text-xs text-gray-400">
            L'adresse email ne peut pas être modifiée après création.
          </p>
        </div>

        <!-- Mot de passe -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700">
            {{ teammate.id ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe' }}
          </label>
          <div class="relative">
            <input
              :required="!teammate.id"
              :type="showPassword ? 'text' : 'password'"
              v-model="teammate.password"
              class="w-full rounded-lg border border-gray-300 px-4 py-3 pr-10 text-sm text-gray-800 shadow-theme-xs"
              :placeholder="teammate.id ? 'Laisser vide pour ne pas modifier' : '••••••••'"
              minlength="6"
            />
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
            </button>
          </div>
          <p v-if="teammate.id" class="mt-1 text-xs text-gray-400">
            Laissez vide pour conserver le mot de passe actuel.
          </p>
        </div>

        <!-- Rôles -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700">
            Rôles
          </label>
          <div class="flex flex-wrap gap-2">
            <label
              v-for="role in availableRoles"
              :key="role.id"
              class="flex items-center gap-2 text-sm bg-gray-100 px-3 py-1 rounded-lg cursor-pointer"
            >
              <input
                type="checkbox"
                :value="role.id"
                v-model="teammate.rolesIds"
              />
              {{ role.name }}
            </label>
          </div>
        </div>

        <!-- Permissions supplémentaires -->
        <div>
          <label class="mb-3 block text-sm font-medium text-gray-700">
            Permissions supplémentaires (en plus des rôles)
          </label>
          <div class="space-y-4">
            <div
              v-for="(category, categoryKey) in displayableCategories"
              :key="categoryKey"
              class="border border-gray-100 rounded-lg p-3"
            >
              <div class="flex items-center gap-2 mb-2">
                <i :class="category.icon" :style="{ color: category.color }"></i>
                <h4 class="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {{ category.label }}
                </h4>
              </div>
              <div class="flex flex-wrap gap-2">
                <label
                  v-for="(permission, permKey) in getPermissionsInCategory(categoryKey)"
                  :key="permKey"
                  class="flex items-center gap-2 text-xs bg-gray-50 hover:bg-gray-100 px-2 py-1.5 rounded border border-gray-200 cursor-pointer"
                  :title="permission.description"
                >
                  <input
                    type="checkbox"
                    :value="permKey"
                    v-model="teammate.permissions"
                    class="rounded size-3"
                  />
                  <span>{{ permission.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Infos -->
        <div class="text-sm text-gray-500 space-y-1">
          <div>
            Créé le: {{ teammate.createdAt ? new Date(teammate.createdAt).toLocaleString() : '-' }}
          </div>
          <div>
            Dernière connexion:
            {{ teammate.lastLogin ? new Date(teammate.lastLogin).toLocaleString() : '-' }}
          </div>
        </div>
      </div>

      <button class="hidden"></button>
    </form>

    <!-- Bouton sauvegarder -->
    <div class="flex justify-end !mt-12">
      <button type="button" @click="startSave" class="buttonComponent w-full">
        Enregistrer le membre
      </button>
    </div>

  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import DataService from '@/features/data/dataService.js'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import displayablePermissions from '@/project/configs/displayablePermissions.json'

const props = defineProps({
  teammateData: { type: Object, default: () => ({}) },
  rolesData: { type: Array, default: () => [] },
})

const emit = defineEmits(['saved'])

/** @type {import('vue').Ref<HTMLFormElement | null>} */
const form = ref(null)
const showPassword = ref(false)

const teammate = reactive({
  id: null,
  name: '',
  email: '',
  password: '',
  rolesIds: [],
  permissions: [],
  createdAt: null,
  lastLogin: null,
})

// Extraire les données displayables
const displayableCategories = computed(() => displayablePermissions.categories)
const allPermissions = computed(() => displayablePermissions.permissions)

// Obtenir les permissions pour une catégorie donnée
/** @param {string} categoryKey */
function getPermissionsInCategory(categoryKey) {
  const permissions = {}
  if (!allPermissions.value) return permissions
  
  Object.entries(allPermissions.value).forEach(([key, permission]) => {
    if (permission.category === categoryKey) {
      permissions[key] = permission
    }
  })
  return permissions
}

// Liste des rôles disponibles
const availableRoles = computed(() => props.rolesData)

watch(
  () => props.teammateData,
  val => {
    if (val && Object.keys(val).length) {
      teammate.id = val.id
      teammate.name = val.name
      teammate.email = val.email
      teammate.password = '' // Clear password on edit
      teammate.rolesIds = val.roles ? val.roles.map(r => r.id) : []
      teammate.permissions = val.permissions || []
      teammate.createdAt = val.createdAt
      teammate.lastLogin = val.lastLogin
    }
  },
  { immediate: true },
)

function startSave() {
  if (!form.value) return
  const isValid = form.value.checkValidity()
  if (!isValid) {
    form.value.reportValidity()
    return
  }
  saveItem()
}

async function saveItem() {
  const roles = (availableRoles.value || []).filter(/** @param {any} r */ r =>
    teammate.rolesIds.includes(r.id),
  )

  const nameParts = (teammate.name || '').trim().split(' ')
  const firstName = nameParts.shift() || ''
  const lastName = nameParts.join(' ') || firstName

  const isUpdate = !!teammate.id

  const userPayload = {
    email: teammate.email,
    firstName: firstName,
    lastName: lastName || 'User',
    password: teammate.password,
  }
  
  if (isUpdate) {
    const userRes = await DataService.triggerAction('updateAdminUser', userPayload)
    if (!userRes || userRes.statusCode !== 200) return
  } else {
    const userRes = await DataService.triggerAction('registerAdminUser', userPayload)
    if (!userRes || (userRes.statusCode !== 200 && userRes.statusCode !== 201)) return
  }

  const teammatePayload = {
    id: teammate.id,
    name: teammate.name,
    email: teammate.email,
    password: teammate.password,
    roles,
    permissions: teammate.permissions,
    createdAt: teammate.createdAt,
    lastLogin: teammate.lastLogin,
  }

  const roleRes = await DataService.triggerAction('saveTeammate', teammatePayload)

  if (roleRes && (roleRes.statusCode === 200 || roleRes.statusCode === 201)) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Succès',
      body: 'Équipier enregistré avec succès',
      type: 'success',
    })
    emit('saved')
  }
}
</script>
